import React, { useState, useEffect } from 'react';
import { mediaAPI } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Newspaper, Play, ExternalLink, Tv } from 'lucide-react';

const getYoutubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
};

const MediaPress = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    mediaAPI.getAll().then(data => { setItems(data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const videos = items.filter(i => i.youtube_url);
  const articles = items.filter(i => !i.youtube_url);

  return (
    <section id="media" className="py-20 px-4 bg-gradient-to-b from-white via-purple-50/30 to-white" data-testid="media-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full text-sm font-semibold mb-4 shadow-lg">
            <Tv className="w-5 h-5" />
            <span>In the Spotlight</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            Media & <span className="text-purple-600">Press Coverage</span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            News coverage, interviews, and media appearances
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <>
            {/* YouTube Videos */}
            {videos.length > 0 && (
              <div className="mb-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Play className="w-6 h-6 text-red-500" /> Video Features
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {videos.map(item => {
                    const videoId = getYoutubeId(item.youtube_url);
                    return (
                      <Card key={item.id} className="overflow-hidden border border-gray-200 hover:shadow-xl transition-all" data-testid={`media-video-${item.id}`}>
                        {videoId && (
                          <div className="aspect-video">
                            <iframe
                              src={`https://www.youtube.com/embed/${videoId}`}
                              title={item.title}
                              className="w-full h-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        )}
                        <CardContent className="p-4">
                          <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                          {item.source && <p className="text-sm text-purple-600 font-medium">{item.source}</p>}
                          {item.description && <p className="text-sm text-gray-600 mt-2">{item.description}</p>}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* News Articles */}
            {articles.length > 0 && (
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Newspaper className="w-6 h-6 text-purple-600" /> News & Articles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {articles.map(item => (
                    <a
                      key={item.id}
                      href={item.external_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group"
                      data-testid={`media-article-${item.id}`}
                    >
                      <Card className="h-full border border-gray-200 hover:shadow-xl hover:-translate-y-1 transition-all">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge className="bg-purple-100 text-purple-700 text-xs">{item.type === 'news' ? 'News' : item.type}</Badge>
                            {item.date && <span className="text-xs text-gray-500">{item.date}</span>}
                          </div>
                          <h4 className="font-bold text-gray-900 mb-2 group-hover:text-purple-700 transition-colors leading-tight">{item.title}</h4>
                          {item.description && <p className="text-sm text-gray-600 mb-3 line-clamp-3">{item.description}</p>}
                          <div className="flex items-center justify-between mt-auto pt-2">
                            {item.source && <span className="text-xs text-purple-600 font-semibold">{item.source}</span>}
                            <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-purple-600 transition-colors" />
                          </div>
                        </CardContent>
                      </Card>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {items.length === 0 && (
              <div className="text-center py-12 text-gray-500">Media coverage will be displayed here.</div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default MediaPress;
